import { Form, Input, Radio } from "antd";
import { AlertCircleIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import CountUp from "react-countup";
import { Trans } from "react-i18next";
import { useTranslation } from "react-i18next";

import { Button, message, Modal } from "@/components";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DEPOSIT_URL } from "@/consts";
import { useGataWallet } from "@/hooks";
import { useUserStore } from "@/stores/user";
import { debounce } from "@/utils/debounce";
import { formatPriceWithoutSymbol } from "@/utils/formatter";
import { paymentRequest } from "@/utils/siwe";

const FEE = {
  DEFAULT: 100,
  MINIMUM: 10,
} as const;

interface FormValues {
  fee?: number;
  customFee?: number;
  eula?: boolean;
}

interface ExchargeModalProps {
  onOk: () => void;
  onClose: () => void;
}

export function ExchargeModal(props: ExchargeModalProps) {
  const { t } = useTranslation();
  const { onOk, onClose } = props;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const [feeFocus, setFeeFocus] = useState<"radio" | "input" | null>("radio");
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState(true);
  const [customFeeDescription, setCustomFeeDescription] = useState<string>(
    `ℹ ${t("billing.rechargeModal.form.10xMsg")}`,
  );
  const [inputValue, setInputValue] = useState<string>("");

  const { swapViaWallet, quoterGataToken, balance } = useGataWallet();

  const silentIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const initialValues = {
    fee: FEE.DEFAULT,
  };
  const [feePoint, setFeePoint] = useState<number>(initialValues.fee || 0);

  const [isExceeded, setIsExceeded] = useState<boolean>(false);

  const debouncedAutoCorrect = useMemo(
    () =>
      debounce((value: number) => {
        const roundedValue = Math.ceil(value / 10) * 10;
        if (roundedValue <= 99999990) {
          form.setFieldsValue({ customFee: roundedValue, fee: undefined });
          setFeePoint(roundedValue);
          setIsCalculating(true);
          setInputValue(roundedValue.toString());
          if (value !== roundedValue) {
            setCustomFeeDescription(
              `✅ ${t("billing.rechargeModal.form.autoCorrectedMsg", {
                value: roundedValue,
              })}`,
            );
          } else {
            setCustomFeeDescription(
              `ℹ ${t("billing.rechargeModal.form.10xMsg")}`,
            );
          }
        } else {
          form.setFieldsValue({ customFee: 99999990, fee: undefined });
          setFeePoint(99999990);
          setIsCalculating(true);
          setInputValue("99999990");
          setCustomFeeDescription(
            "✅ Automatically corrected to 99999990 (This field accepts only multiples of 10)",
          );
        }
      }, 800),
    [],
  );

  const onValueChange = (values: Partial<FormValues>) => {
    const { fee, customFee } = values || {};

    if ("fee" in values) {
      setFeePoint(fee || 0);
      setIsCalculating(true);
      if (fee !== undefined) {
        form.setFieldsValue({ customFee: undefined });
        debouncedFetchPaymentAmount(fee);
      }
    }

    if ("customFee" in values) {
      setFeePoint(customFee || 0);
      setIsCalculating(true);
      if (customFee !== undefined) {
        form.setFieldsValue({ fee: undefined });
      }
    }
  };

  const userStore = useUserStore();

  useEffect(() => {
    if (feeFocus === "radio") {
      setInputValue("");
      form.setFieldsValue({ customFee: undefined });
    }
  }, [feeFocus]);

  const _onOK = async () => {
    const formRes = await form.validateFields();
    if (formRes) {
      setLoading(true);
      try {
        // await swapViaWallet(1, 0, '0xee4695823d7a2e5871b66df0f02095aaf709a25c')

        const res = await paymentRequest(feePoint);
        if (res.code === 0) {
          const { address, amount_with_fees } = res.data || {};
          const gataAmount = await quoterGataToken(amount_with_fees);
          console.log("[quoterGataToken] amount_with_fees:", {
            amount_with_fees,
            gataAmount,
          });
          if (!gataAmount) {
            throw Error("Failed to request payment.");
          }
          await swapViaWallet(gataAmount, 0, address);
          message.success("Payment successful");
          onOk();
        } else {
          console.error("Payment request error:", {
            feePoint,
            paymentAmount,
            res,
          });
          message.error(res.msg || "Failed to request payment.");
        }
      } catch (error) {
        console.error("Payment processing error:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Get payment amount
  const fetchPaymentAmount = async (creditCent: number) => {
    try {
      const amountOut = await quoterGataToken(creditCent);
      if (amountOut) {
        setPaymentAmount(amountOut);
      }
    } catch (error) {
      console.error("Failed to fetch payment amount:", error);
    } finally {
      setIsCalculating(false);
    }
  };

  const silentFetchLatestPrice = async (creditCent: number) => {
    try {
      const latestAmountOut = await quoterGataToken(creditCent);
      // console.log('[Silent Polling] , creditCent:', creditCent, '-> amountOut:', latestAmountOut)
      if (latestAmountOut) {
        setPaymentAmount(latestAmountOut);
      }
      setIsExceeded(false);
    } catch (error: any) {
      console.error("[Silent Polling] error:", error);
      if (error?.reason === "Unexpected error") {
        try {
          // Workaround, check if pool is exceeded.
          await quoterGataToken(0.01);
          console.log("quoter checker pass");
          setIsExceeded(true);
        } catch (error) {
          setIsExceeded(false);
        }
      }
    }
  };

  // Wrap fetchPaymentAmount with debounce
  const debouncedFetchPaymentAmount = useMemo(
    () => debounce(fetchPaymentAmount, 300),
    [],
  );

  useEffect(() => {
    if (feePoint > 0) {
      const intervalMs = 1000; // 1s
      silentIntervalRef.current = setInterval(() => {
        silentFetchLatestPrice(feePoint);
      }, intervalMs);
    } else {
      if (silentIntervalRef.current) {
        clearInterval(silentIntervalRef.current);
        silentIntervalRef.current = null;
      }
    }
    return () => {
      if (silentIntervalRef.current) {
        clearInterval(silentIntervalRef.current);
        silentIntervalRef.current = null;
      }
    };
  }, [feePoint]);

  // Update payment amount when currency or amount changes
  useEffect(() => {
    if (feePoint > 0) {
      debouncedFetchPaymentAmount(feePoint);
    }
  }, [feePoint, debouncedFetchPaymentAmount]);

  // Calculate payment amount on component mount
  useEffect(() => {
    if (initialValues.fee > 0) {
      debouncedFetchPaymentAmount(initialValues.fee);
    }
  }, []); // Only execute once when component mounts

  const { currentErrorMessage } = useMemo(() => {
    if (isCalculating || feePoint <= 0) {
      return {
        hasInsufficientBalance: false,
        hasExceededLimit: false,
        currentErrorMessage: "",
      };
    }

    if (paymentAmount > balance) {
      return {
        hasInsufficientBalance: true,
        hasExceededLimit: false,
        currentErrorMessage:
          "⚠️ Insufficient balance, please deposit GATA token to your account.",
      };
    }

    if (isExceeded) {
      return {
        hasInsufficientBalance: false,
        hasExceededLimit: true,
        currentErrorMessage:
          "⚠️ You've exceeded the credits purchase limit, please reduce amount.",
      };
    }

    return {
      hasInsufficientBalance: false,
      hasExceededLimit: false,
      currentErrorMessage: "",
    };
  }, [isCalculating, feePoint, paymentAmount, balance, isExceeded]);

  return (
    <Modal
      className="w-[640px] h-fit"
      open={true}
      onClose={() => {
        if (!loading) {
          onClose();
        }
      }}
    >
      <Modal.Header>
        <Trans i18nKey="billing.rechargeModal.title">Recharge</Trans>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto scrollbar-hide pb-14">
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onValuesChange={onValueChange}
          validateTrigger="onBlur"
          scrollToFirstError
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="t-sb text-with-star">
              <Trans i18nKey="billing.rechargeModal.form.amount">
                Buy Credits
              </Trans>
            </div>
            <div className="t-sm text-secondary py-1 px-4 flex flex-row gap-2">
              <Trans i18nKey="billing.rechargeModal.myBalance">
                My Balance
              </Trans>
              <span className="t-sb text-highlight">
                {userStore.account.totalBonus.moneyCent > 1000000000 ? (
                  <span>9,999,999+</span>
                ) : (
                  <CountUp
                    end={formatPriceWithoutSymbol(
                      userStore.account.totalBonus.moneyCent ?? 0,
                    )}
                    separator=","
                    decimals={2}
                  />
                )}
              </span>

              <span className="t-sm text-secondary">
                {userStore.account.totalBonus.moneyCent > 100
                  ? "Credits"
                  : "Credit"}
              </span>
            </div>
          </div>
          <Form.Item
            className="mb-2"
            name="fee"
            rules={[
              {
                validator: (_, value) => {
                  const customFee = form.getFieldValue("customFee");
                  if (customFee !== undefined && customFee !== "") {
                    return Promise.resolve();
                  }
                  if (feeFocus !== "radio" && value < FEE.MINIMUM) {
                    return Promise.reject(
                      new Error(
                        t("billing.rechargeModal.form.minimumRechargeMsg"),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Radio.Group className="w-full grid grid-cols-4 gap-2">
              <Radio
                value={FEE.DEFAULT}
                className="flex justify-center text-center px-4 py-6 h-22 border border-neutral-400 rounded-lg hover:border-[#2bcaa5] [&_.ant-radio]:hidden [&.ant-radio-wrapper-checked]:border-[#2bcaa5] [&.ant-radio-wrapper-checked]:bg-[#F1FCF8]"
                onClick={() => {
                  setFeeFocus("radio");
                  setInputValue("");
                  form.setFieldsValue({ customFee: undefined });
                }}
              >
                <div className="t-bold text-primary">100</div>
                <div className="t-xs text-secondary">Credits</div>
              </Radio>
              <Radio
                value={50}
                className="flex justify-center text-center px-4 py-6 h-22 border border-neutral-400 rounded-lg hover:border-[#2bcaa5] [&_.ant-radio]:hidden [&.ant-radio-wrapper-checked]:border-[#2bcaa5] [&.ant-radio-wrapper-checked]:bg-[#F1FCF8]"
                onClick={() => {
                  setFeeFocus("radio");
                  setInputValue("");
                  form.setFieldsValue({ customFee: undefined });
                }}
              >
                <div className="t-bold text-primary">50</div>
                <div className="t-xs text-secondary">Credits</div>
              </Radio>
              <Radio
                value={30}
                className="flex justify-center text-center px-4 py-6 h-22 border border-neutral-400 rounded-lg hover:border-[#2bcaa5] [&_.ant-radio]:hidden [&.ant-radio-wrapper-checked]:border-[#2bcaa5] [&.ant-radio-wrapper-checked]:bg-[#F1FCF8]"
                onClick={() => {
                  setFeeFocus("radio");
                  setInputValue("");
                  form.setFieldsValue({ customFee: undefined });
                }}
              >
                <div className="t-bold text-primary">30</div>
                <div className="t-xs text-secondary">Credits</div>
              </Radio>
              <Radio
                value={FEE.MINIMUM}
                className="flex justify-center text-center px-4 py-6 h-22 border border-neutral-400 rounded-lg hover:border-[#2bcaa5] [&_.ant-radio]:hidden [&.ant-radio-wrapper-checked]:border-[#2bcaa5] [&.ant-radio-wrapper-checked]:bg-[#F1FCF8]"
                onClick={() => {
                  setFeeFocus("radio");
                  setInputValue("");
                  form.setFieldsValue({ customFee: undefined });
                }}
              >
                <div className="t-bold text-primary">10</div>
                <div className="t-xs text-secondary">Credits</div>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="mb-4"
            name="customFee"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === "") {
                    // Allow empty string if radio button is selected
                    return Promise.resolve();
                  }
                  const numValue = Number(value);
                  if (isNaN(numValue)) {
                    return Promise.reject(
                      new Error(t("billing.rechargeModal.form.wrongNumberMsg")),
                    );
                  }
                  if (numValue < FEE.MINIMUM) {
                    return Promise.reject(
                      new Error(
                        t("billing.rechargeModal.form.minimumRechargeMsg"),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <div>
              <Input
                size="large"
                suffix={<p className="t-sb text-primary">Credits</p>}
                inputMode="numeric"
                maxLength={8}
                max={999_999_990}
                placeholder={t("billing.rechargeModal.form.amountPlaceholder")}
                className="w-full items-center t-sb px-4 py-3 h-11 border rounded-lg hover:border-secondary"
                onFocus={() => {
                  setFeeFocus("input");
                  form.setFieldsValue({ fee: undefined, customFee: undefined });
                  setInputValue("");
                  debouncedFetchPaymentAmount.cancel();
                  setFeePoint(0);
                  setPaymentAmount(0);
                  setIsCalculating(true);
                  setCustomFeeDescription(
                    t("billing.rechargeModal.form.10xMsg"),
                  );
                }}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "") {
                    debouncedAutoCorrect.cancel();
                    debouncedFetchPaymentAmount.cancel();
                    form.setFieldsValue({
                      customFee: undefined,
                      fee: undefined,
                    });
                    setFeePoint(0);
                    setInputValue("");
                    setIsCalculating(true);
                    setCustomFeeDescription(
                      `ℹ ${t("billing.rechargeModal.form.10xMsg")}`,
                    );
                    return;
                  }

                  setInputValue(value);
                  const numValue = Number(value);
                  if (!isNaN(numValue)) {
                    if (numValue === 0) {
                      form.setFieldsValue({
                        customFee: undefined,
                        fee: undefined,
                      });
                      setFeePoint(0);
                      setCustomFeeDescription(
                        `ℹ ${t("billing.rechargeModal.form.10xMsg")}`,
                      );
                      return;
                    }
                    if (numValue <= 99999990) {
                      if (numValue % 10 !== 0) {
                        setCustomFeeDescription(
                          `⚠️ ${t(
                            "billing.rechargeModal.form.notMultipleOf10Msg",
                          )}`,
                        );
                        debouncedAutoCorrect(numValue);
                      } else {
                        debouncedAutoCorrect.cancel();
                        form.setFieldsValue({
                          customFee: numValue,
                          fee: undefined,
                        });
                        setFeePoint(numValue);
                        setIsCalculating(true);
                        setCustomFeeDescription(
                          `ℹ ${t("billing.rechargeModal.form.10xMsg")}`,
                        );
                      }
                    } else {
                      debouncedAutoCorrect.cancel();
                      form.setFieldsValue({
                        customFee: 99999990,
                        fee: undefined,
                      });
                      setFeePoint(99999990);
                      setIsCalculating(true);
                      setInputValue("99999990");
                      setCustomFeeDescription(
                        `✅ ${t("billing.rechargeModal.form.10xMsg")}`,
                      );
                    }
                  }
                }}
                value={feeFocus !== "radio" ? inputValue : ""}
              />
              <div className="mt-2 t-sm text-secondary">
                {customFeeDescription}
              </div>
            </div>
          </Form.Item>
        </Form>

        <div className="mb-2 flex items-center justify-between">
          <div className="t-sb text-with-star">Payment</div>
          <div className="t-sm flex flex-row text-secondary py-1 px-4 gap-2">
            Available
            <div className="flex flex-row gap-1">
              <span className="t-sb text-primary">
                <CountUp end={balance} separator="," decimals={0} />
              </span>
              <span className="t-sb text-secondary">GATA</span>
            </div>
            <a href={DEPOSIT_URL} target="_blank" rel="noopener noreferrer">
              <span className="t-sm text-highlight">Deposit</span>
            </a>
          </div>
        </div>
        <div className="mb-4">
          <div className="bg-secondary rounded-lg items-center justify-center p-4">
            <div className="flex flex-col items-center">
              <div className="h4 text-[#2bcaa5] gap-2">
                {isCalculating ? (
                  <span>Calculating...</span>
                ) : (
                  <CountUp
                    end={paymentAmount}
                    separator=","
                    decimals={2}
                    suffix=" GATA"
                  />
                )}
              </div>
              <div className="t-xs text-tertiary">
                <Trans i18nKey="billing.rechargeModal.estimatedAmountPayable">
                  Estimated amount payable
                </Trans>
              </div>
            </div>
          </div>

          {currentErrorMessage && (
            <div className="t-s-sb text-[#E5484D] mt-2">
              {currentErrorMessage}
            </div>
          )}
        </div>

        <Alert className="mb-4 p-4 border border-[#2bcaa5] bg-[#F1FCF8] text-panel-primary [&>svg]:text-panel-primary">
          <AlertCircleIcon className="size-5" />
          <AlertTitle hidden />
          <AlertDescription>
            Spend available token from your Gata account.
          </AlertDescription>
        </Alert>

        <div className="flex items-end justify-end gap-4 absolute bottom-0 left-8 right-8 bg-[#fff] pb-8">
          <Button
            type="outline"
            size="md"
            onClick={() => {
              if (!loading) {
                onClose();
              }
            }}
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type="secondary"
            size="md"
            disabled={
              loading ||
              isCalculating || // Disable when calculating
              !Number.parseInt(`${feePoint}`) ||
              form.getFieldValue("fee") < 10
            }
            onClick={_onOK}
          >
            <Trans i18nKey="common.confirmModalRecharge">Recharge</Trans>
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
